import React, { useEffect, useState } from "react";
import { KycFunctionType, PermissionCategory } from "../../http/protocol";
import { getContainer } from "../../ioc/IOCSetup";
import { PermissionRequired } from "../security/PermissionRequired";
import { t } from "../../i18n";
import { getKycFunctionTypeDisplayName } from "../../util/KycFunctionTypeUtils";

interface Props {
    userId: number;
}

export const UserKycFunctionComponent: React.FC<Props> = ({ userId }) => {
    const [loading, setLoading] = useState<boolean>(true);
    const [saving, setSaving] = useState<boolean>(false);
    const [kycFunctions, setKycFunctions] = useState<KycFunctionType[]>([]);
    const [error, setError] = useState<string | undefined>();
    const [selection, setSelection] = useState<KycFunctionType | "">("");

    const userService = getContainer().getUserService();

    const fetchKycFunctions = async () => {
        setLoading(true);
        setError(undefined);
        try {
            const list = await userService.getKycFunctionTypesForUser(userId);
            setKycFunctions(list || []);
        } catch (e: any) {
            setError(e?.message || "Failed to load KYC functions");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchKycFunctions();
    }, [userId]);

    const handleAdd = async () => {
        if (!selection) return;
        try {
            setSaving(true);
            setError(undefined);
            await userService.addKycFunctionTypes(userId, [selection as KycFunctionType]);
            await fetchKycFunctions();
            setSelection("");
        } catch (e: any) {
            setError(e?.message || "Failed to add KYC function");
        } finally {
            setSaving(false);
        }
    };

    const handleRemove = async (functionType: KycFunctionType) => {
        try {
            setSaving(true);
            setError(undefined);
            await userService.removeKycFunctionType(userId, functionType);
            await fetchKycFunctions();
        } catch (e: any) {
            setError(e?.message || "Failed to remove KYC function");
        } finally {
            setSaving(false);
        }
    };

    const selectable = Object.values(KycFunctionType).filter(functionType => !kycFunctions.includes(functionType as KycFunctionType));
    const hasAll = kycFunctions.includes(KycFunctionType.ALL);

    return (
        <div className="card mb-3">
            <div className="card-header">
                <h4 className="mb-0">KYC Functions</h4>
            </div>
            <div className="card-body">
                <div className="ibox-content">
                {error && <div className="alert alert-danger" role="alert">{error}</div>}
                {loading ? (
                    <div>{t("label.loading", { defaultValue: "Loading..." })}</div>
                ) : (
                    <PermissionRequired permissions={[PermissionCategory.USER_KYC_STATUS]} accessLevel="w" showError={false}>
                        <div>
                            {kycFunctions.length === 0 && (
                                <div className="text-muted mb-2">{t("label.noKycFunctions", { defaultValue: "No KYC functions set" })}</div>
                            )}
                            {kycFunctions.map(functionType => (
                                <div className="d-flex align-items-center mb-2" key={functionType}>
                                    <span className="form-control" style={{ flex: 1 }}>{getKycFunctionTypeDisplayName(functionType)}</span>
                                    <button
                                        className="btn btn-outline-danger ml-2"
                                        disabled={saving}
                                        onClick={() => handleRemove(functionType)}
                                    >
                                        {t("button.remove", { defaultValue: "Remove" })}
                                    </button>
                                </div>
                            ))}
                        </div>
                        <div className="d-flex align-items-center">
                            <select 
                                className="form-control"
                                value={selection}
                                onChange={e => setSelection((e.target.value || "") as any)}
                                disabled={hasAll || saving}
                            >
                                <option value="">{t("label.selectKycFunction", { defaultValue: "Select KYC function" })}</option>
                                {selectable.map(functionType => (
                                    <option key={functionType} value={functionType}>{getKycFunctionTypeDisplayName(functionType as KycFunctionType)}</option>
                                ))}
                            </select>
                            <button 
                                className="btn btn-primary ml-2"
                                onClick={handleAdd}
                                disabled={hasAll || saving || !selection}
                            >
                                {t("button.add", { defaultValue: "Add" })}
                            </button>
                        </div>
                    </PermissionRequired>
                )}
                </div>
            </div>
        </div>
    );
};

export default UserKycFunctionComponent;
